import { generatedTranslations } from './locales/generatedTranslations';
import { localeManualOverrides } from './locales/manualOverrides';

const SUPPORTED_LOCALES = [
  'en',
  'en-AU',
  'en-GB',
  'es',
  'fr',
  'fr-CA',
  'de',
  'ja',
  'zh-Hans',
  'zh-Hant',
  'zh-HK'
] as const;

type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
const LOCALE_STORAGE_KEY = 'creator_portal_locale';

export const LOCALE_OPTIONS: Array<{ value: SupportedLocale; labelKey: string }> = [
  { value: 'en', labelKey: 'locale.en' },
  { value: 'zh-HK', labelKey: 'locale.zhHk' },
  { value: 'zh-Hans', labelKey: 'locale.zhHans' },
  { value: 'zh-Hant', labelKey: 'locale.zhHant' },
  { value: 'en-AU', labelKey: 'locale.enAu' },
  { value: 'en-GB', labelKey: 'locale.enGb' },
  { value: 'fr', labelKey: 'locale.fr' },
  { value: 'fr-CA', labelKey: 'locale.frCa' },
  { value: 'de', labelKey: 'locale.de' },
  { value: 'ja', labelKey: 'locale.ja' },
  { value: 'es', labelKey: 'locale.es' }
];

type Messages = Record<string, string>;

const messages: Record<string, Messages> = {
  en: {
    'common.loading': 'Loading...',
    'common.email': 'Email',
    'common.password': 'Password',
    'common.confirmPassword': 'Confirm Password',
    'common.backToLogin': 'Back to Sign in',
    'common.signIn': 'Sign in',
    'common.emailExample': 'creator@example.com',
    'common.clearSearch': 'Clear search',

    'login.title': 'Welcome back',
    'login.subtitle': 'Sign in to your creator account',
    'login.passwordPlaceholder': 'Enter your password',
    'login.forgotPassword': 'Forgot password?',
    'login.submit': 'Sign In',
    'login.noAccount': "Don't have an account?",
    'login.createOne': 'Create one',
    'login.error.verifyEmail': 'Please verify your email before logging in. Check your inbox for the verification link.',
    'login.error.invalidCredentials': 'Invalid email or password.',
    'login.resend.button': 'Resend verification email',
    'login.resend.success': 'If your account exists and is not verified, a new verification email has been sent.',
    'login.resend.error': 'Failed to resend verification email. Please try again shortly.',
    'login.resend.wait': 'Please wait a moment before requesting another verification email.',
    'login.resend.waitWithSeconds': 'Resend available in',

    'register.title': 'Create your account',
    'register.titleSuccess': 'Check your email',
    'register.subtitle': 'Join the X-periences creator community',
    'register.subtitleSuccess': 'We sent you a verification link',
    'register.alias': 'Alias',
    'register.aliasPlaceholder': 'Your creator alias',
    'register.passwordPlaceholder': 'At least 6 characters',
    'register.confirmPasswordPlaceholder': 'Confirm your password',
    'register.acceptPrefix': 'I have read and accept the',
    'register.terms': 'Terms of Use',
    'register.and': 'and',
    'register.privacy': 'Privacy Policy',
    'register.acceptSuffix': '.',
    'register.submit': 'Create Account',
    'register.successCreated': 'Account created successfully!',
    'register.successSentTo': "We've sent a verification email to:",
    'register.successCheckInbox': 'Please check your inbox (and spam folder) and click the verification link to activate your account.',
    'register.successEmailIssue': 'Your account was created, but verification email delivery is temporarily unavailable. Use the resend verification button from login in a moment.',
    'register.successBackToLogin': 'Back to Login',
    'register.haveAccount': 'Already have an account?',
    'register.signIn': 'Sign in',
    'register.error.passwordsMismatch': 'Passwords do not match',
    'register.error.passwordMin': 'Password must be at least 6 characters',
    'register.error.acceptTerms': 'You must read and accept the Terms of Use and Privacy Policy',
    'register.error.generic': 'Registration failed',

    'forgot.title': 'Reset Password',
    'forgot.subtitle': 'Enter your email to receive a reset link',
    'forgot.submit': 'Send Reset Link',
    'forgot.success': 'Check your email for a password reset link.',
    'forgot.devMode': 'Development mode:',
    'forgot.devGoReset': 'Go to Reset Password',
    'forgot.remember': 'Remember your password?',
    'forgot.error.generic': 'Failed to send reset email',

    'reset.title': 'Reset Password',
    'reset.subtitle': 'Enter your new password',
    'reset.newPassword': 'New Password',
    'reset.confirmPassword': 'Confirm Password',
    'reset.confirmPlaceholder': 'Confirm your new password',
    'reset.submit': 'Reset Password',
    'reset.success': 'Password reset successfully! Redirecting to login...',
    'reset.error.invalidLink': 'Invalid reset link',
    'reset.error.passwordsMismatch': 'Passwords do not match',
    'reset.error.passwordMin': 'Password must be at least 6 characters',
    'reset.error.invalidToken': 'Invalid reset token',
    'reset.error.generic': 'Failed to reset password',

    'verify.title': 'Email Verification',
    'verify.loading': 'Verifying your email...',
    'verify.successLine1': 'Your email has been verified successfully!',
    'verify.successLine2': 'You can now log in to your account.',
    'verify.redirecting': 'Redirecting to login...',
    'verify.alreadyLine1': 'Your email was already verified!',
    'verify.alreadyLine2': 'You can log in to your account.',
    'verify.goLogin': 'Go to Login',
    'verify.error.noToken': 'No verification token provided',
    'verify.error.generic': 'Verification failed',

    'navbar.subtitle': 'Creator Portal',
    'navbar.dashboard': 'Dashboard',
    'navbar.events': 'Events',
    'navbar.modXperiences': 'Mod. Xperiences',
    'navbar.manageCreators': 'Manage Creators',
    'navbar.newXperience': 'New Xperience',
    'navbar.generateCreators': 'Generate Creators',
    'navbar.generateXperiences': 'Generate Xperiences',
      'navbar.generate': 'Generate',
    'navbar.generating': 'Generating',
    'navbar.profile': 'Profile',
    'navbar.logout': 'Logout',
    'navbar.confirmGenerateCreators': 'Are you sure you want to generate creators JSON?',
    'navbar.confirmGenerateXperiences': 'Are you sure you want to generate xperiences JSON?',
    'navbar.successGenerateCreators': 'Creators generated successfully!',
    'navbar.successGenerateXperiences': 'Xperiences generated successfully!',
    'navbar.error.endpoint': 'Endpoint not found. The API service might need a restart.',
    'navbar.error.noBody': 'No response body',
    'navbar.error.generateCreators': 'Failed to generate creators',
    'navbar.error.generateXperiences': 'Failed to generate xperiences',
    'navbar.roleDefault': 'Creator',
    'navbar.statusPending': 'Pending',
    'navbar.statusTitle': 'Status',
    'navbar.totalViews': 'Total Views',
    'navbar.avgRating': 'Average Rating',
    'navbar.roleTitle': 'Role',
    'navbar.languageLabel': 'Language',
    'navbar.lang.en': 'English',
    'navbar.lang.es': 'Spanish',
    'locale.en': 'English',
    'locale.zhHk': 'Chinese (Hong Kong)',
    'locale.zhHans': 'Chinese, Simplified',
    'locale.zhHant': 'Chinese, Traditional',
    'locale.enAu': 'English (Australia)',
    'locale.enGb': 'English (United Kingdom)',
    'locale.fr': 'French',
    'locale.frCa': 'French (Canada)',
    'locale.de': 'German',
    'locale.ja': 'Japanese',
    'locale.es': 'Spanish',

    'home.profileIncomplete': 'Please complete your profile before managing xperiences',
    'home.error.load': 'Failed to load xperiences',
    'home.title': 'Your Xperiences',
    'home.subtitle': 'Manage your immersive content for Apple Vision Pro',
    'home.newXperience': '+ New Xperience',
    'home.searchPlaceholder': 'Search by title or description...',
    'home.clearSearch': 'Clear search',
    'home.allMediaTypes': 'All Media Types',
    'home.loading': 'Loading xperiences...',
    'home.emptyTitle': 'No xperiences yet',
    'home.emptyText': 'Create your first immersive xperience for Apple Vision Pro',
    'home.createFirst': 'Create First Xperience',
    'home.noResultsTitle': 'No results found',
    'home.noResultsText': 'Try adjusting your search criteria or filters',
    'home.clearFilters': 'Clear Filters',
    'home.privateTitle': 'Private Xperience',
    'home.categoryTitle': 'Category',
    'home.viewsTitle': 'Views',
    'home.likesTitle': 'Likes',
    'home.dislikesTitle': 'Dislikes',
    'home.ratingTitle': 'Rating',
    'home.viewDetails': 'View Details',
    'home.moreTags': 'more tags',
    'home.files': 'file',
    'home.filesPlural': 'files',

    'events.title': 'Events',
    'events.subtitle': 'Group Xperiences into private client deliveries for weddings, corporate events, baptisms, and immersive productions.',
    'events.newEvent': '+ New Event',
    'events.loading': 'Loading events...',
    'events.emptyTitle': 'No events yet',
    'events.emptyText': 'Create a private event and add the Xperiences you want to deliver together.',
    'events.createTitle': 'Create Event',
    'events.editTitle': 'Edit Event',
    'events.editorSubtitle': 'Prepare the private delivery space, location, and included Xperiences.',
    'events.backToList': 'Back to Events',
    'events.error.load': 'Failed to load events',
    'events.error.loadXperiences': 'Failed to load xperiences',
    'events.error.save': 'Failed to save event',
    'events.error.delete': 'Failed to delete event',
    'events.error.coverUpload': 'Failed to upload event poster',
    'events.error.posterRead': 'Failed to open event poster image',
    'events.error.posterCrop': 'Failed to crop event poster',
    'events.error.emailQR': 'Failed to send event QR email.',
    'events.success.emailSent': 'Event QR email sent successfully.',
    'events.success.passwordCopied': 'Password copied to clipboard.',
    'events.confirmDelete': 'Delete this event?',
    'events.public': 'Public',
    'events.private': 'Private',
    'events.xperiences': 'xperiences',
    'events.mediaItems': 'media items',
    'events.edit': 'Edit',
    'events.delete': 'Delete',
    'events.password': 'Password',
    'events.saving': 'Saving...',
    'events.save': 'Save Event',
    'events.reset': 'New Event',
    'events.cancel': 'Cancel',
    'events.copyPassword': 'Copy Password',
    'events.emailQR': 'Email QR',
    'events.sendingEmail': 'Sending email...',
    'events.privateToggle': 'Private event with password access',
    'events.field.title': 'Title',
    'events.field.description': 'Description',
    'events.field.type': 'Event type',
    'events.field.date': 'Event date',
    'events.field.locationName': 'Location name',
    'events.field.location': 'Location',
    'events.field.poster': 'Event poster',
    'events.field.coverUrl': 'Cover image path',
    'events.field.password': 'Access password',
    'events.field.xperiences': 'Included Xperiences',
    'events.posterHelp': 'Upload the wide poster shown in the Vision Pro event hero.',
    'events.posterEmpty': 'No event poster selected',
    'events.posterSizeHint': 'Recommended image: 1920 x 420 px. The upload is cropped to this panoramic ratio.',
    'events.uploadPoster': 'Upload poster',
    'events.uploadingPoster': 'Uploading poster...',
    'events.posterCropTitle': 'Adjust event poster',
    'events.posterCropZoom': 'Zoom',
    'events.posterCropUse': 'Use poster',
    'events.privateQR.title': 'Private Event Access QR',
    'events.privateQR.description': 'Share this QR or password to grant private access to this event.',
    'events.privateQR.alt': 'Private event access QR code',
    'events.privateQR.saveBeforeEmail': 'Save the event before emailing its QR.',
    'events.xperiencePickerHelp': 'Search and select the Xperiences that belong to this event delivery.',
    'events.searchXperiences': 'Search Xperiences',
    'events.searchXperiencesPlaceholder': 'Search by title or description...',
    'events.searchingXperiences': 'Searching Xperiences...',
    'events.availableXperiences': 'Available Xperiences',
    'events.selectedXperiences': 'Selected Xperiences',
    'events.selected': 'selected',
    'events.reorderXperience': 'Drag to reorder',
    'events.noXperienceResults': 'No Xperiences found. Try a different search.',
    'events.placeholder.password': 'Leave empty to auto-generate',
    'events.type.wedding': 'Wedding',
    'events.type.baptism': 'Baptism',
    'events.type.corporate': 'Corporate event',
    'events.type.privateEvent': 'Private event',

    'beCreator.title': 'Be a Creator on X-periences',
    'beCreator.subtitle': 'Share immersive experiences for Apple Vision Pro',
    'beCreator.point1': 'Publish your own xperiences and build your audience.',
    'beCreator.point2': 'Track views, likes, and ratings from your dashboard.',
    'beCreator.point3': 'Manage media and updates from one creator portal.',
    'beCreator.ctaPrimary': 'Create Creator Account',
    'beCreator.ctaSecondary': 'I already have an account',
    'beCreator.heroTitle': 'Become a Creator',
    'beCreator.heroSubtitle': 'Share your unique X-periences with the world and turn your passion into reality.',
    'beCreator.heroCta': 'Join as a Creator',
    'beCreator.benefitsTitle': 'Why Join X-periences?',
    'beCreator.benefit.audienceTitle': 'Reach a Global Audience',
    'beCreator.benefit.audienceText': 'Connect with users worldwide who are eager to discover and engage with your unique content and X-periences.',
    'beCreator.benefit.recognitionTitle': 'Earn Recognition',
    'beCreator.benefit.recognitionText': 'Build your reputation as a creator by sharing immersive stories that inspire users around the world.',
    'beCreator.benefit.toolsTitle': 'Powerful Creator Tools',
    'beCreator.benefit.toolsText': 'Access advanced analytics, seamless media management, and easy-to-use publishing tools tailored for creators.',
    'beCreator.benefit.communityTitle': 'Supportive Community',
    'beCreator.benefit.communityText': 'Join a network of passionate creators. Share ideas, collaborate, and grow together in a safe environment.',
    'beCreator.howItWorksTitle': 'How It Works',
    'beCreator.step.signupTitle': 'Sign Up',
    'beCreator.step.signupText': 'Create your creator profile in just a few clicks.',
    'beCreator.step.createTitle': 'Create',
    'beCreator.step.createText': 'Upload your content and build your engaging X-periences.',
    'beCreator.step.recognizedTitle': 'Get Recognized',
    'beCreator.step.recognizedText': 'Share your xperiences globally, grow your audience, and become a reference creator in immersive content.',
    'beCreator.footerTitle': 'Ready to start your journey?',
    'beCreator.footerText': 'Take the first step to share immersive xperiences with the world.',
    'beCreator.footerCta': 'Get Started Now',

    'profile.loading': 'Loading profile...',
    'profile.title': 'Creator Profile',
    'profile.subtitle': 'Manage your creator information',
    'profile.requiredFieldsLabel': 'Required fields*:',
    'profile.requiredFieldsText': 'Full Name, Avatar, Biography, and Country are required to access the Xperiences dashboard.',
    'profile.section.accountInfo': 'Account Information',
    'profile.section.changePassword': 'Change Password',
    'profile.section.personalInfo': 'Personal Information',
    'profile.section.appSocial': 'App & Social Media',
    'profile.section.creatorStats': 'Creator Statistics',
    'profile.field.usernameAlias': 'Username (Alias)',
    'profile.field.currentPassword': 'Current Password',
    'profile.field.newPassword': 'New Password',
    'profile.field.confirmNewPassword': 'Confirm New Password',
    'profile.field.fullNameRequired': 'Full Name *',
    'profile.field.phone': 'Phone',
    'profile.field.countryRequired': 'Country *',
    'profile.field.avatarRequired': 'Avatar *',
    'profile.field.biographyRequired': 'Biography *',
    'profile.field.appName': 'App Name',
    'profile.field.appUrl': 'App URL',
    'profile.field.youtubeUrl': 'YouTube URL',
    'profile.field.instagramUrl': 'Instagram URL',
    'profile.field.otherUrl': 'Other URL',
    'profile.placeholder.username': 'Your username',
    'profile.placeholder.currentPassword': 'Enter your current password',
    'profile.placeholder.newPassword': 'Enter new password',
    'profile.placeholder.confirmNewPassword': 'Confirm new password',
    'profile.placeholder.fullName': 'Your full name',
    'profile.placeholder.phone': '+1 (555) 000-0000',
    'profile.placeholder.searchCountries': 'Type to search countries...',
    'profile.placeholder.biography': 'Tell us about yourself...',
    'profile.placeholder.appName': 'e.g., My App',
    'profile.placeholder.appUrl': 'https://myapp.com',
    'profile.placeholder.youtubeUrl': 'https://youtube.com/@yourusername',
    'profile.placeholder.instagramUrl': 'https://instagram.com/yourusername',
    'profile.placeholder.otherUrl': 'https://example.com',
    'profile.note.publicUsername': 'Your public username',
    'profile.note.emailImmutable': 'Email address cannot be changed in profile',
    'profile.note.passwordMin': 'Minimum 6 characters',
    'profile.note.requiredForDashboard': 'Required to access dashboard',
    'profile.note.countryRequired': 'Required to access dashboard • Select from list or type to search',
    'profile.note.avatarRequired': 'Required to access dashboard • JPG, PNG, or GIF (max 20MB)',
    'profile.note.biographyRequiredPrefix': 'Required to access dashboard •',
    'profile.note.characters': 'characters',
    'profile.optional': 'Optional',
    'profile.allOptional': 'All Optional',
    'profile.uploading': 'Uploading...',
    'profile.uploadingAvatar': 'Uploading avatar...',
    'profile.chooseAvatarImage': 'Choose Avatar Image',
    'profile.avatarPreviewAlt': 'Avatar Preview',
    'profile.preview': 'PREVIEW',
    'profile.openInNewTab': 'Open in new tab',
    'profile.view': 'View',
    'profile.stats.rating': 'Rating',
    'profile.stats.credits': 'Credits',
    'profile.stats.views': 'Views',
    'profile.stats.votes': 'Votes',
    'profile.saving': 'Saving...',
    'profile.saveChanges': 'Save Changes',
    'profile.success.profileAndPasswordUpdated': 'Profile and password updated successfully!',
    'profile.success.profileUpdatedComplete': 'Profile updated successfully! All required fields are complete. You can now access the Xperiences dashboard.',
    'profile.success.profileUpdatedIncomplete': 'Profile updated successfully! Please complete all required fields (Name, Avatar, Biography, Country) to access the dashboard.',
    'profile.error.loadProfile': 'Failed to load profile',
    'profile.error.avatarUpload': 'Failed to upload avatar. Profile NOT saved.',
    'profile.error.fillAllPasswordFields': 'Please fill all password fields to change your password',
    'profile.error.passwordsMismatch': 'New passwords do not match',
    'profile.error.passwordMin': 'New password must be at least 6 characters',
    'profile.error.changePassword': 'Failed to change password',
    'profile.error.profileUpdatedPasswordFailed': 'Profile updated but password change failed',
    'profile.error.updateProfile': 'Failed to update profile. Please try again.',

    'adminCreators.title': 'Manage Creators',
    'adminCreators.loading': 'Loading creators...',
    'adminCreators.error.load': 'Failed to load creators',
    'adminCreators.error.roleUpdate': 'Failed to update role',
    'adminCreators.error.statusUpdate': 'Failed to update status',
    'adminCreators.error.trustUpdate': 'Failed to update trust flag',
    'adminCreators.success.roleUpdated': 'Role updated successfully!',
    'adminCreators.success.statusUpdated': 'Status updated successfully!',
    'adminCreators.success.trustUpdated': 'Trust flag updated successfully!',
    'adminCreators.table.alias': 'Alias',
    'adminCreators.table.email': 'Email',
    'adminCreators.table.emailVerified': 'Email Verified',
    'adminCreators.table.hidden': 'Hidden',
    'adminCreators.table.joinDate': 'Join Date',
    'adminCreators.table.role': 'Role',
    'adminCreators.table.status': 'Status',
    'adminCreators.table.trusted': 'Trusted',
    'adminCreators.yes': 'Yes',
    'adminCreators.no': 'No',
    'adminCreators.hidden': 'Hidden',
    'adminCreators.visible': 'Visible',
    'adminCreators.trusted': 'Trusted',
    'adminCreators.standard': 'Standard',
    'adminCreators.role.creator': 'Creator',
    'adminCreators.role.manager': 'Manager',
    'adminCreators.role.administrator': 'Administrator',
    'adminCreators.status.pending': 'Pending',
    'adminCreators.status.approved': 'Approved',
    'adminCreators.status.rejected': 'Banned / Rejected',
    'adminCreators.empty': 'No creators found.',
    'adminCreators.pagination.page': 'Page',
    'adminCreators.pagination.of': 'of',
    'adminCreators.pagination.total': 'Total',
    'adminCreators.pagination.rowsPerPage': 'Rows per page',
    'adminCreators.pagination.previous': 'Previous',
    'adminCreators.pagination.next': 'Next',

    'adminCreatorDetail.title': 'Edit Creator:',
    'adminCreatorDetail.back': 'Back',
    'adminCreatorDetail.id': 'ID',
    'adminCreatorDetail.email': 'Email',
    'adminCreatorDetail.alias': 'Alias',
    'adminCreatorDetail.fullName': 'Full Name',
    'adminCreatorDetail.phone': 'Phone',
    'adminCreatorDetail.countryCode': 'Country Code',
    'adminCreatorDetail.countryPlaceholder': 'Type to search countries...',
    'adminCreatorDetail.flags': 'Profile Flags',
    'adminCreatorDetail.emailVerified': 'Email Verified',
    'adminCreatorDetail.resendEmail': 'Resend Email',
    'adminCreatorDetail.sending': 'Sending...',
    'adminCreatorDetail.hideProfile': 'Hide Profile (Incomplete)',
    'adminCreatorDetail.isTrusted': 'Trusted Creator',
    'adminCreatorDetail.bio': 'Bio / Description',
    'adminCreatorDetail.linksApps': 'Links & Apps',
    'adminCreatorDetail.appName': 'App Name',
    'adminCreatorDetail.appUrl': 'App URL',
    'adminCreatorDetail.youtubeUrl': 'YouTube URL',
    'adminCreatorDetail.instagramUrl': 'Instagram URL',
    'adminCreatorDetail.otherUrl': 'Other URL',
    'adminCreatorDetail.saveProfile': 'Save Profile',
    'adminCreatorDetail.saving': 'Saving...',
    'adminCreatorDetail.error.load': 'Failed to load creator',
    'adminCreatorDetail.error.verificationSend': 'Failed to send verification email.',
    'adminCreatorDetail.error.update': 'Failed to update creator',
    'adminCreatorDetail.success.verificationSent': 'Verification email sent successfully.',
    'adminCreatorDetail.success.updated': 'Creator updated successfully',

    'moderation.back': 'Back',
    'moderation.title.single': 'Moderating Xperience Content',
    'moderation.title.global': 'Content Moderation',
    'moderation.searchPlaceholder': 'Search by title, description, or creator...',
    'moderation.filter.allMedia': 'All Media',
    'moderation.filter.pending': 'Pending',
    'moderation.filter.approved': 'Approved',
    'moderation.filter.rejected': 'Rejected',
    'moderation.loading': 'Loading media...',
    'moderation.empty.search': 'No media match your search.',
    'moderation.empty.filter': 'No media found for the selected filter.',
    'moderation.noPoster': 'No Poster',
    'moderation.untitled': 'Untitled',
    'moderation.noDescription': 'No description',
    'moderation.creator': 'Creator',
    'moderation.email': 'Email',
    'moderation.date': 'Date',
    'moderation.na': 'N/A',
    'moderation.viewOriginal': 'View Original File',
    'moderation.action.approve': 'Approve',
    'moderation.action.reject': 'Reject',
    'moderation.action.setPending': 'Set Pending',
    'moderation.success.markedAs': 'Media successfully marked as',
    'moderation.error.loadMedia': 'Error loading media for moderation.',
    'moderation.error.updateStatus': 'Failed to update media status.',

    'adminXperiences.backDashboard': 'Back to Dashboard',
    'adminXperiences.title': 'Global Xperiences Moderation',
    'adminXperiences.searchPlaceholder': 'Search by title, description, or creator...',
    'adminXperiences.filter.all': 'All Xperiences',
    'adminXperiences.filter.pending': 'Pending',
    'adminXperiences.filter.approved': 'Approved',
    'adminXperiences.filter.rejected': 'Rejected',
    'adminXperiences.loading': 'Loading xperiences...',
    'adminXperiences.empty.search': 'No xperiences match your search.',
    'adminXperiences.empty.filter': 'No xperiences found for the selected filter.',
    'adminXperiences.noPoster': 'No Poster',
    'adminXperiences.noDescription': 'No description',
    'adminXperiences.creator': 'Creator',
    'adminXperiences.email': 'Email',
    'adminXperiences.date': 'Date',
    'adminXperiences.na': 'N/A',
    'adminXperiences.mediaCount': 'Media Count',
    'adminXperiences.items': 'items',
    'adminXperiences.action.moderateMedia': 'Moderate Media',
    'adminXperiences.action.approveAll': 'Approve All',
    'adminXperiences.action.rejectAll': 'Reject All',
    'adminXperiences.action.setPending': 'Set Pending',
    'adminXperiences.success.markedAs': 'Xperience successfully marked as',
    'adminXperiences.error.fetch': 'Failed to fetch xperiences',
    'adminXperiences.error.updateStatus': 'Failed to update status',

    'createXperience.profileIncomplete': 'Please complete your profile before creating a xperience',
    'createXperience.error.create': 'Failed to create xperience',
    'createXperience.title': 'Create New Xperience',
    'createXperience.subtitle': 'Start building your immersive content',
    'createXperience.field.title': 'Title',
    'createXperience.placeholder.title': 'Xperience title',
    'createXperience.field.description': 'Description',
    'createXperience.placeholder.description': 'Describe your xperience...',
    'createXperience.field.location': 'Location',
    'createXperience.field.tags': 'Tags',
    'createXperience.copyLastLocation': 'Copy last location',
    'createXperience.error.noLastLocation': 'No previous location available yet.',
    'createXperience.placeholder.tags': 'Select or search tags...',
    'createXperience.help.tags': 'Choose one or more tags to improve discovery.',
    'createXperience.cancel': 'Cancel',
    'createXperience.submit': 'Create Xperience',

    'xperienceDetail.success.mediaOrderSaved': 'Media order saved successfully.',
    'xperienceDetail.error.mediaOrderSave': 'Failed to save media order.',
    'xperienceDetail.scenes.title': 'Scenes',
    'xperienceDetail.scenes.subtitle': 'Group media into ordered moments for Vision Pro playback.',
    'xperienceDetail.scenes.newLabel': 'New scene',
    'xperienceDetail.scenes.newPlaceholder': 'For example: Ceremony, Cocktail, Dance floor...',
    'xperienceDetail.scenes.create': 'Create Scene',
    'xperienceDetail.scenes.save': 'Save',
    'xperienceDetail.scenes.moveUp': 'Up',
    'xperienceDetail.scenes.moveDown': 'Down',
    'xperienceDetail.scenes.selectMedia': 'Select media to add',
    'xperienceDetail.scenes.noAvailableMedia': 'All media is already in this scene',
    'xperienceDetail.scenes.addMedia': 'Add Media',
    'xperienceDetail.scenes.removeMedia': 'Remove',
    'xperienceDetail.scenes.emptyMedia': 'No media in this scene yet.',
    'xperienceDetail.scenes.empty': 'No scenes created yet. Create scenes to organize this Xperience for guided playback.',
    'xperienceDetail.scenes.untitledMedia': 'Untitled media',
    'xperienceDetail.scenes.confirmDelete': 'Delete this scene? Media files will remain available in the Xperience.',
    'xperienceDetail.scenes.success.created': 'Scene created.',
    'xperienceDetail.scenes.success.updated': 'Scene updated.',
    'xperienceDetail.scenes.success.deleted': 'Scene deleted.',
    'xperienceDetail.scenes.success.mediaAdded': 'Media added to scene.',
    'xperienceDetail.scenes.success.mediaRemoved': 'Media removed from scene.',
    'xperienceDetail.scenes.error.save': 'Failed to save scene.',
    'xperienceDetail.scenes.error.reorder': 'Failed to reorder scenes.',
    'xperienceDetail.scenes.error.reorderMedia': 'Failed to reorder scene media.',
    'xperienceDetail.error.load': 'Failed to load xperience',
    'xperienceDetail.success.updated': 'Xperience updated successfully.',
    'xperienceDetail.error.update': 'Failed to update xperience',
    'xperienceDetail.sendingEmail': 'Sending email...',
    'xperienceDetail.success.emailSent': 'QR access email sent successfully.',
    'xperienceDetail.success.locationCopiedToAll': 'Location copied to {count} media items.',
    'xperienceDetail.error.sendEmail': 'Failed to send QR email.',
    'xperienceDetail.error.uploadMedia': 'Failed to upload media',
    'xperienceDetail.error.deleteMedia': 'Failed to delete media',
    'xperienceDetail.error.deleteXperience': 'Failed to delete xperience',
    'xperienceDetail.error.noLocationToCopyAll': 'Set a location first before copying it to all media.',
    'xperienceDetail.error.invalidLocationToCopyAll': 'Current location coordinates are invalid.',
    'xperienceDetail.error.noMediaToCopyLocationAll': 'No media items found to update in this xperience.',
    'xperienceDetail.error.locationCopiedToAllPartial': 'Updated {success} media items, but {failed} failed.',
    'xperienceDetail.error.locationCopiedToAllFailed': 'Could not update location for all media.',
    'xperienceDetail.loading': 'Loading xperience...',
    'xperienceDetail.notFound': 'Xperience not found',
    'xperienceDetail.backDashboard': 'Back to Dashboard',
    'xperienceDetail.editTitle': 'Edit Xperience',
    'xperienceDetail.field.title': 'Title',
    'xperienceDetail.field.description': 'Description',
    'xperienceDetail.field.location': 'Location',
    'xperienceDetail.field.tags': 'Tags',
    'xperienceDetail.copyLastLocation': 'Copy last location',
    'xperienceDetail.copyLocationToAll': 'Copy location to all media',
    'xperienceDetail.copyLocationToAllLoading': 'Copying to all media...',
    'xperienceDetail.error.noLastLocation': 'No previous location available yet.',
    'xperienceDetail.success.lastLocationCopied': 'Last location copied.',
    'xperienceDetail.placeholder.tags': 'Select or search tags...',
    'xperienceDetail.help.tags': 'Choose one or more tags to improve discovery.',
    'xperienceDetail.privateToggle': 'Private xperience (requires password)',
    'xperienceDetail.cancel': 'Cancel',
    'xperienceDetail.saveChanges': 'Save Changes',
    'xperienceDetail.edit': 'Edit',
    'xperienceDetail.deleteXperience': 'Delete Xperience',
    'xperienceDetail.info.status': 'Status',
    'xperienceDetail.pending': 'Pending',
    'xperienceDetail.info.views': 'Views',
    'xperienceDetail.info.rating': 'Rating',
    'xperienceDetail.info.reactions': 'Reactions',
    'xperienceDetail.info.tags': 'Tags',
    'xperienceDetail.info.category': 'Category',
    'xperienceDetail.info.created': 'Created',
    'xperienceDetail.noDate': 'N/A',
    'xperienceDetail.info.mediaCount': 'Media Count',
    'xperienceDetail.info.visibility': 'Visibility',
    'xperienceDetail.visibility.private': 'Private',
    'xperienceDetail.visibility.public': 'Public',
    'xperienceDetail.privateQR.title': 'Private Access QR',
    'xperienceDetail.privateQR.description': 'Share this QR or password to grant private access to your xperience.',
    'xperienceDetail.privateQR.alt': 'Private access QR code',
    'xperienceDetail.success.passwordCopied': 'Password copied to clipboard.',
    'xperienceDetail.copyPassword': 'Copy Password',
    'xperienceDetail.wait': 'Wait',
    'xperienceDetail.emailQR': 'Email QR',
    'xperienceDetail.mediaSectionTitle': 'Xperience Media',
    'xperienceDetail.createMedia': '+ Create Media',
    'xperienceDetail.uploading': 'Uploading',
    'xperienceDetail.noMedia': 'No media added yet.',
    'xperienceDetail.noMediaHelp': 'No media files yet. Upload images, videos, or audio.',
    'xperienceDetail.mediaCard.added': 'Added',
    'xperienceDetail.mediaCard.na': 'N/A',
    'xperienceDetail.mediaCard.viewDetails': 'View Details',
    'xperienceDetail.confirm.deleteMediaTitle': 'Delete media?',
    'xperienceDetail.confirm.deleteMediaMessage': 'This media item will be permanently deleted. This action cannot be undone.',
    'xperienceDetail.confirm.deleteXperienceTitle': 'Delete xperience?',
    'xperienceDetail.confirm.deleteXperienceMessage': 'This xperience and all associated media will be permanently deleted. This action cannot be undone.',
    'xperienceDetail.confirm.delete': 'Delete',
    'xperienceDetail.confirm.cancel': 'Cancel',
    'xperienceDetail.confirm.copyLocationAllTitle': 'Copy location to all media?',
    'xperienceDetail.confirm.copyLocationAllMessage': 'This will update location coordinates for all media in this xperience. Continue?',
    'xperienceDetail.confirm.copyLocationAllConfirm': 'Yes, update all media',
    'xperienceDetail.confirm.copyLocationAllCancel': 'Cancel',

    'xmedia.error.loadData': 'Failed to load data',
    'xmedia.error.delete': 'Failed to delete media',
    'xmedia.error.save': 'Failed to save xperience media',
    'xmedia.error.uploadTooLarge': 'The media file is too large for the current upload limit.',
    'xmedia.error.noXperienceLocation': 'The parent xperience has no location to copy yet.',
    'xmedia.error.noLastLocation': 'No previous location available yet.',
    'xmedia.error.requiredPrefix': 'The following fields are required:',
    'xmedia.success.posterSelected': 'Poster selected locally. It will be uploaded on save.',
    'xmedia.success.mediaSelected': 'Media selected locally. It will be uploaded on save.',
    'xmedia.success.deleted': 'Media deleted successfully!',
    'xmedia.success.created': 'Xperience media created and files uploaded successfully!',
    'xmedia.success.updated': 'Xperience media updated successfully!',
    'xmedia.success.locationCopied': 'Location copied from xperience.',
    'xmedia.success.lastLocationCopied': 'Last location copied.',
    'xmedia.confirm.deleteMessage': 'Are you sure you want to permanently delete this media item and its associated files? This action cannot be undone.',
    'xmedia.loading': 'Loading xperience media...',
    'xmedia.notFound': 'Xperience media not found',
    'xmedia.back': 'Back',
    'xmedia.title.create': 'Create New Media',
    'xmedia.title.edit': 'Edit Xperience Media',
    'xmedia.subtitle': 'Update all metadata and files for this media item.',
    'xmedia.field.title': 'Title',
    'xmedia.field.description': 'Description',
    'xmedia.field.type': 'Type',
    'xmedia.field.format': 'Format',
    'xmedia.field.category': 'Category',
    'xmedia.field.mediaFile': 'Media File',
    'xmedia.field.posterUrl': 'Poster URL',
    'xmedia.field.order': 'Order',
    'xmedia.field.runtime': 'Runtime',
    'xmedia.field.credits': 'Credits / Points required',
    'xmedia.field.location': 'Location',
    'xmedia.field.createdDate': 'Created Date',
    'xmedia.placeholder.title': 'Enter a title',
    'xmedia.placeholder.description': 'Enter description...',
    'xmedia.placeholder.mediaUrl': 'URL will be generated safely on save',
    'xmedia.placeholder.posterUrl': 'Poster path will be generated on save',
    'xmedia.placeholder.order': 'e.g., 1',
    'xmedia.placeholder.runtime': 'e.g., 3600',
    'xmedia.placeholder.credits': 'e.g., 0',
    'xmedia.selectFile': 'Select File',
    'xmedia.changeFile': 'Change File',
    'xmedia.viewMedia': 'View Media',
    'xmedia.selectImage': 'Select Image',
    'xmedia.changeImage': 'Change Image',
    'xmedia.thumbnail': 'Thumbnail Image',
    'xmedia.seconds': 'seconds',
    'xmedia.requiresDownload': 'Requires Download',
    'xmedia.isPublic': 'Is Public',
    'xmedia.isPremium': 'Is Premium',
    'xmedia.hide': 'Hide',
    'xmedia.deleteMedia': 'Delete Media',
    'xmedia.cancel': 'Cancel',
    'xmedia.saving': 'Saving...',
    'xmedia.saveChanges': 'Save Changes',
    'xmedia.copyXperienceLocation': 'Copy xperience location',
    'xmedia.copyLastLocation': 'Copy last location',
    'xmedia.copyLocationToAll': 'Copy location to all media',
    'xmedia.copyLocationToAllLoading': 'Copying to all media...',
    'xmedia.locationSource.xperience': 'Using xperience primary location',
    'xmedia.locationSource.last': 'Using last copied location',
    'xmedia.lastLocationPreview': 'Saved last location',
    'xmedia.confirm.copyLocationAllTitle': 'Copy location to all media?',
    'xmedia.confirm.copyLocationAllMessage': 'This will update location coordinates for all media in this xperience. Continue?',
    'xmedia.confirm.copyLocationAllConfirm': 'Yes, update all media',
    'xmedia.confirm.copyLocationAllCancel': 'Cancel',
    'xmedia.success.locationCopiedToAll': 'Location copied to {count} media items.',
    'xmedia.upload.selected': 'Selected',
    'xmedia.upload.keepOpen': 'Keep this browser tab open until the upload finishes.',
    'xmedia.upload.poster': 'Poster upload',
    'xmedia.upload.media': 'Media upload',
    'xmedia.upload.speed': 'Speed',
    'xmedia.upload.remaining': 'Time remaining',
    'xmedia.upload.phase.idle': 'Ready to upload',
    'xmedia.upload.phase.metadata': 'Saving media metadata...',
    'xmedia.upload.phase.poster': 'Uploading poster...',
    'xmedia.upload.phase.media': 'Uploading immersive media...',
    'xmedia.upload.phase.finalizing': 'Finalizing upload...',
    'xmedia.error.noXperienceForBulkLocation': 'Could not find the parent xperience for bulk location update.',
    'xmedia.error.noLocationToCopyAll': 'Set a location first before copying it to all media.',
    'xmedia.error.invalidLocationToCopyAll': 'Current location coordinates are invalid.',
    'xmedia.error.noMediaToCopyLocationAll': 'No media items found to update in this xperience.',
    'xmedia.error.locationCopiedToAllPartial': 'Updated {success} media items, but {failed} failed.',
    'xmedia.error.locationCopiedToAllFailed': 'Could not update location for all media.',

    'locationPicker.searchLabel': 'Search address',
    'locationPicker.searchPlaceholder': 'Type an address, city, or place',
    'locationPicker.longitude': 'Longitude',
    'locationPicker.latitude': 'Latitude',
    'locationPicker.clear': 'Clear location',
    'locationPicker.useCurrent': 'Use my current location',
    'locationPicker.locating': 'Locating...',
    'locationPicker.geoNotSupported': 'Your browser does not support geolocation.',
    'locationPicker.geoError': 'Could not get your current location. Check browser permissions.',
    'locationPicker.selectedAddress': 'Selected address',
    'locationPicker.loadingMap': 'Loading map...',
    'locationPicker.noApiKey': 'Google Maps API key is missing',
    'locationPicker.noApiKeyHelp': 'Set REACT_APP_GOOGLE_MAPS_API_KEY in frontend env to enable map and address search.',
    'locationPicker.loadError': 'Could not load Google Maps',
    'locationPicker.hint': 'Tip: search an address, click on the map, or drag the marker to set precise coordinates.'
  },
  es: {
    'common.loading': 'Cargando...',
    'common.email': 'Correo electronico',
    'common.password': 'Contrasena',
    'common.confirmPassword': 'Confirmar contrasena',
    'common.backToLogin': 'Volver a iniciar sesion',
    'common.signIn': 'Iniciar sesion',
    'common.emailExample': 'creator@example.com',
    'common.clearSearch': 'Limpiar busqueda',

    'login.title': 'Bienvenido de nuevo',
    'login.subtitle': 'Inicia sesion en tu cuenta de creador',
    'login.passwordPlaceholder': 'Introduce tu contrasena',
    'login.forgotPassword': 'Olvidaste tu contrasena?',
    'login.submit': 'Iniciar sesion',
    'login.noAccount': 'No tienes una cuenta?',
    'login.createOne': 'Crear una',
    'login.error.verifyEmail': 'Verifica tu correo antes de iniciar sesion. Revisa tu bandeja de entrada para el enlace de verificacion.',
    'login.error.invalidCredentials': 'Correo o contrasena invalidos.',
    'login.resend.button': 'Reenviar correo de verificacion',
    'login.resend.success': 'Si tu cuenta existe y no esta verificada, hemos enviado un nuevo correo de verificacion.',
    'login.resend.error': 'No se pudo reenviar el correo de verificacion. Intentalo en unos minutos.',
    'login.resend.wait': 'Espera un momento antes de solicitar otro correo de verificacion.',
    'login.resend.waitWithSeconds': 'Podras reenviar en',

    'register.title': 'Crea tu cuenta',
    'register.titleSuccess': 'Revisa tu correo',
    'register.subtitle': 'Unete a la comunidad de creadores de X-periences',
    'register.subtitleSuccess': 'Te enviamos un enlace de verificacion',
    'register.alias': 'Alias',
    'register.aliasPlaceholder': 'Tu alias de creador',
    'register.passwordPlaceholder': 'Al menos 6 caracteres',
    'register.confirmPasswordPlaceholder': 'Confirma tu contrasena',
    'register.acceptPrefix': 'He leido y acepto los',
    'register.terms': 'Terminos de uso',
    'register.and': 'y la',
    'register.privacy': 'Politica de privacidad',
    'register.acceptSuffix': '.',
    'register.submit': 'Crear cuenta',
    'register.successCreated': 'Cuenta creada correctamente!',
    'register.successSentTo': 'Hemos enviado un correo de verificacion a:',
    'register.successCheckInbox': 'Revisa tu bandeja de entrada (y spam) y haz clic en el enlace para activar tu cuenta.',
    'register.successEmailIssue': 'Tu cuenta se ha creado, pero el envio del correo de verificacion no esta disponible temporalmente. Usa el boton de reenvio desde login en un momento.',
    'register.successBackToLogin': 'Volver al inicio de sesion',
    'register.haveAccount': 'Ya tienes una cuenta?',
    'register.signIn': 'Iniciar sesion',
    'register.error.passwordsMismatch': 'Las contrasenas no coinciden',
    'register.error.passwordMin': 'La contrasena debe tener al menos 6 caracteres',
    'register.error.acceptTerms': 'Debes leer y aceptar los Terminos de uso y la Politica de privacidad',
    'register.error.generic': 'No se pudo completar el registro',

    'forgot.title': 'Restablecer contrasena',
    'forgot.subtitle': 'Introduce tu correo para recibir un enlace de restablecimiento',
    'forgot.submit': 'Enviar enlace',
    'forgot.success': 'Revisa tu correo para restablecer la contrasena.',
    'forgot.devMode': 'Modo desarrollo:',
    'forgot.devGoReset': 'Ir a restablecer contrasena',
    'forgot.remember': 'Recuerdas tu contrasena?',
    'forgot.error.generic': 'No se pudo enviar el correo de restablecimiento',

    'reset.title': 'Restablecer contrasena',
    'reset.subtitle': 'Introduce tu nueva contrasena',
    'reset.newPassword': 'Nueva contrasena',
    'reset.confirmPassword': 'Confirmar contrasena',
    'reset.confirmPlaceholder': 'Confirma tu nueva contrasena',
    'reset.submit': 'Restablecer contrasena',
    'reset.success': 'Contrasena restablecida correctamente. Redirigiendo al inicio de sesion...',
    'reset.error.invalidLink': 'Enlace de restablecimiento invalido',
    'reset.error.passwordsMismatch': 'Las contrasenas no coinciden',
    'reset.error.passwordMin': 'La contrasena debe tener al menos 6 caracteres',
    'reset.error.invalidToken': 'Token de restablecimiento invalido',
    'reset.error.generic': 'No se pudo restablecer la contrasena',

    'verify.title': 'Verificacion de correo',
    'verify.loading': 'Verificando tu correo...',
    'verify.successLine1': 'Tu correo fue verificado correctamente!',
    'verify.successLine2': 'Ya puedes iniciar sesion en tu cuenta.',
    'verify.redirecting': 'Redirigiendo al inicio de sesion...',
    'verify.alreadyLine1': 'Tu correo ya estaba verificado!',
    'verify.alreadyLine2': 'Puedes iniciar sesion en tu cuenta.',
    'verify.goLogin': 'Ir a iniciar sesion',
    'verify.error.noToken': 'No se proporciono token de verificacion',
    'verify.error.generic': 'No se pudo verificar el correo',

    'navbar.subtitle': 'Portal de creadores',
    'navbar.dashboard': 'Panel',
    'navbar.events': 'Eventos',
    'navbar.modXperiences': 'Mod. Xperiences',
    'navbar.manageCreators': 'Gestionar creadores',
    'navbar.newXperience': 'Nueva Xperience',
    'navbar.generateCreators': 'Generar creadores',
    'navbar.generateXperiences': 'Generar xperiences',
      'navbar.generate': 'Generar',
    'navbar.generating': 'Generando',
    'navbar.profile': 'Perfil',
    'navbar.logout': 'Cerrar sesion',
    'navbar.confirmGenerateCreators': 'Seguro que deseas generar el JSON de creadores?',
    'navbar.confirmGenerateXperiences': 'Seguro que deseas generar el JSON de xperiences?',
    'navbar.successGenerateCreators': 'Creadores generados correctamente!',
    'navbar.successGenerateXperiences': 'Xperiences generadas correctamente!',
    'navbar.error.endpoint': 'No se encontro el endpoint. El servicio API puede necesitar reinicio.',
    'navbar.error.noBody': 'La respuesta no contiene cuerpo',
    'navbar.error.generateCreators': 'No se pudieron generar creadores',
    'navbar.error.generateXperiences': 'No se pudieron generar xperiences',
    'navbar.roleDefault': 'Creador',
    'navbar.statusPending': 'Pendiente',
    'navbar.statusTitle': 'Estado',
    'navbar.totalViews': 'Vistas totales',
    'navbar.avgRating': 'Valoracion media',
    'navbar.roleTitle': 'Rol',
    'navbar.languageLabel': 'Idioma',
    'navbar.lang.en': 'Ingles',
    'navbar.lang.es': 'Espanol',
    'locale.en': 'Ingles',
    'locale.zhHk': 'Chino (Hong Kong)',
    'locale.zhHans': 'Chino simplificado',
    'locale.zhHant': 'Chino tradicional',
    'locale.enAu': 'Ingles (Australia)',
    'locale.enGb': 'Ingles (Reino Unido)',
    'locale.fr': 'Frances',
    'locale.frCa': 'Frances (Canada)',
    'locale.de': 'Aleman',
    'locale.ja': 'Japones',
    'locale.es': 'Espanol',

    'home.profileIncomplete': 'Completa tu perfil antes de gestionar xperiences',
    'home.error.load': 'No se pudieron cargar las xperiences',
    'home.title': 'Tus Xperiences',
    'home.subtitle': 'Gestiona tu contenido inmersivo para Apple Vision Pro',
    'home.newXperience': '+ Nueva Xperience',
    'home.searchPlaceholder': 'Buscar por titulo o descripcion...',
    'home.clearSearch': 'Limpiar busqueda',
    'home.allMediaTypes': 'Todos los tipos de media',
    'home.loading': 'Cargando xperiences...',
    'home.emptyTitle': 'Aun no tienes xperiences',
    'home.emptyText': 'Crea tu primera xperience inmersiva para Apple Vision Pro',
    'home.createFirst': 'Crear primera Xperience',
    'home.noResultsTitle': 'No se encontraron resultados',
    'home.noResultsText': 'Prueba ajustando los filtros o la busqueda',
    'home.clearFilters': 'Limpiar filtros',
    'home.privateTitle': 'Xperience privada',
    'home.categoryTitle': 'Categoria',
    'home.viewsTitle': 'Vistas',
    'home.likesTitle': 'Me gusta',
    'home.dislikesTitle': 'No me gusta',
    'home.ratingTitle': 'Valoracion',
    'home.viewDetails': 'Ver detalles',
    'home.moreTags': 'mas etiquetas',
    'home.files': 'archivo',
    'home.filesPlural': 'archivos',

    'events.title': 'Eventos',
    'events.subtitle': 'Agrupa Xperiences en entregas privadas para clientes de bodas, eventos de empresa, bautizos y producciones inmersivas.',
    'events.newEvent': '+ Nuevo evento',
    'events.loading': 'Cargando eventos...',
    'events.emptyTitle': 'Aun no tienes eventos',
    'events.emptyText': 'Crea un evento privado y anade las Xperiences que quieres entregar juntas.',
    'events.createTitle': 'Crear evento',
    'events.editTitle': 'Editar evento',
    'events.editorSubtitle': 'Prepara el espacio privado de entrega, la ubicacion y las Xperiences incluidas.',
    'events.backToList': 'Volver a eventos',
    'events.error.load': 'No se pudieron cargar los eventos',
    'events.error.loadXperiences': 'No se pudieron cargar las xperiences',
    'events.error.save': 'No se pudo guardar el evento',
    'events.error.delete': 'No se pudo eliminar el evento',
    'events.error.coverUpload': 'No se pudo subir el poster del evento',
    'events.error.posterRead': 'No se pudo abrir la imagen del poster',
    'events.error.posterCrop': 'No se pudo recortar el poster del evento',
    'events.error.emailQR': 'No se pudo enviar el email con el QR del evento.',
    'events.success.emailSent': 'Email con QR del evento enviado correctamente.',
    'events.success.passwordCopied': 'Password copiado al portapapeles.',
    'events.confirmDelete': 'Eliminar este evento?',
    'events.public': 'Publico',
    'events.private': 'Privado',
    'events.xperiences': 'xperiences',
    'events.mediaItems': 'elementos de media',
    'events.edit': 'Editar',
    'events.delete': 'Eliminar',
    'events.password': 'Password',
    'events.saving': 'Guardando...',
    'events.save': 'Guardar evento',
    'events.reset': 'Nuevo evento',
    'events.cancel': 'Cancelar',
    'events.copyPassword': 'Copiar password',
    'events.emailQR': 'Enviar QR por email',
    'events.sendingEmail': 'Enviando email...',
    'events.privateToggle': 'Evento privado con acceso por password',
    'events.field.title': 'Titulo',
    'events.field.description': 'Descripcion',
    'events.field.type': 'Tipo de evento',
    'events.field.date': 'Fecha del evento',
    'events.field.locationName': 'Nombre de la ubicacion',
    'events.field.location': 'Ubicacion',
    'events.field.poster': 'Poster del evento',
    'events.field.coverUrl': 'Ruta de imagen de portada',
    'events.field.password': 'Password de acceso',
    'events.field.xperiences': 'Xperiences incluidas',
    'events.posterHelp': 'Sube el poster panoramico que se muestra en la hero del evento en Vision Pro.',
    'events.posterEmpty': 'No hay poster seleccionado',
    'events.posterSizeHint': 'Imagen recomendada: 1920 x 420 px. La subida se recorta a esta proporcion panoramica.',
    'events.uploadPoster': 'Subir poster',
    'events.uploadingPoster': 'Subiendo poster...',
    'events.posterCropTitle': 'Ajustar poster del evento',
    'events.posterCropZoom': 'Zoom',
    'events.posterCropUse': 'Usar poster',
    'events.privateQR.title': 'QR de acceso privado al evento',
    'events.privateQR.description': 'Comparte este QR o password para dar acceso privado a este evento.',
    'events.privateQR.alt': 'Codigo QR de acceso privado al evento',
    'events.privateQR.saveBeforeEmail': 'Guarda el evento antes de enviar su QR por email.',
    'events.xperiencePickerHelp': 'Busca y selecciona las Xperiences que pertenecen a esta entrega del evento.',
    'events.searchXperiences': 'Buscar Xperiences',
    'events.searchXperiencesPlaceholder': 'Buscar por titulo o descripcion...',
    'events.searchingXperiences': 'Buscando Xperiences...',
    'events.availableXperiences': 'Xperiences disponibles',
    'events.selectedXperiences': 'Xperiences seleccionadas',
    'events.selected': 'seleccionadas',
    'events.reorderXperience': 'Arrastrar para reordenar',
    'events.noXperienceResults': 'No se encontraron Xperiences. Prueba otra busqueda.',
    'events.placeholder.password': 'Dejar vacio para autogenerar',
    'events.type.wedding': 'Boda',
    'events.type.baptism': 'Bautizo',
    'events.type.corporate': 'Evento de empresa',
    'events.type.privateEvent': 'Evento privado',

    'beCreator.title': 'Conviertete en creador en X-periences',
    'beCreator.subtitle': 'Comparte experiencias inmersivas para Apple Vision Pro',
    'beCreator.point1': 'Publica tus propias xperiences y construye tu audiencia.',
    'beCreator.point2': 'Consulta vistas, me gusta y valoraciones en tu panel.',
    'beCreator.point3': 'Gestiona media y actualizaciones desde un unico portal de creadores.',
    'beCreator.ctaPrimary': 'Crear cuenta de creador',
    'beCreator.ctaSecondary': 'Ya tengo una cuenta',
    'beCreator.heroTitle': 'Conviertete en creador',
    'beCreator.heroSubtitle': 'Comparte tus X-periences con el mundo y convierte tu pasion en realidad.',
    'beCreator.heroCta': 'Unirme como creador',
    'beCreator.benefitsTitle': 'Por que unirte a X-periences?',
    'beCreator.benefit.audienceTitle': 'Llega a una audiencia global',
    'beCreator.benefit.audienceText': 'Conecta con usuarios de todo el mundo que quieren descubrir e interactuar con tu contenido y tus X-periences.',
    'beCreator.benefit.recognitionTitle': 'Gana reconocimiento',
    'beCreator.benefit.recognitionText': 'Construye tu reputacion como creador compartiendo historias inmersivas que inspiren a usuarios de todo el mundo.',
    'beCreator.benefit.toolsTitle': 'Herramientas potentes para creadores',
    'beCreator.benefit.toolsText': 'Accede a analiticas avanzadas, gestion de media sin friccion y herramientas de publicacion faciles de usar.',
    'beCreator.benefit.communityTitle': 'Comunidad que apoya',
    'beCreator.benefit.communityText': 'Unete a una red de creadores apasionados. Comparte ideas, colabora y crece en un entorno seguro.',
    'beCreator.howItWorksTitle': 'Como funciona',
    'beCreator.step.signupTitle': 'Registrate',
    'beCreator.step.signupText': 'Crea tu perfil de creador en pocos clics.',
    'beCreator.step.createTitle': 'Crea',
    'beCreator.step.createText': 'Sube tu contenido y construye X-periences atractivas.',
    'beCreator.step.recognizedTitle': 'Hazte referente',
    'beCreator.step.recognizedText': 'Comparte tus xperiences globalmente, crece tu audiencia y conviertete en un creador de referencia.',
    'beCreator.footerTitle': 'Listo para empezar tu viaje?',
    'beCreator.footerText': 'Da el primer paso para compartir xperiences inmersivas con el mundo.',
    'beCreator.footerCta': 'Empezar ahora',

    'profile.loading': 'Cargando perfil...',
    'profile.title': 'Perfil de creador',
    'profile.subtitle': 'Gestiona tu informacion de creador',
    'profile.requiredFieldsLabel': 'Campos obligatorios*:',
    'profile.requiredFieldsText': 'Nombre completo, Avatar, Biografia y Pais son obligatorios para acceder al panel de Xperiences.',
    'profile.section.accountInfo': 'Informacion de la cuenta',
    'profile.section.changePassword': 'Cambiar contrasena',
    'profile.section.personalInfo': 'Informacion personal',
    'profile.section.appSocial': 'App y redes sociales',
    'profile.section.creatorStats': 'Estadisticas del creador',
    'profile.field.usernameAlias': 'Nombre de usuario (Alias)',
    'profile.field.currentPassword': 'Contrasena actual',
    'profile.field.newPassword': 'Nueva contrasena',
    'profile.field.confirmNewPassword': 'Confirmar nueva contrasena',
    'profile.field.fullNameRequired': 'Nombre completo *',
    'profile.field.phone': 'Telefono',
    'profile.field.countryRequired': 'Pais *',
    'profile.field.avatarRequired': 'Avatar *',
    'profile.field.biographyRequired': 'Biografia *',
    'profile.field.appName': 'Nombre de la app',
    'profile.field.appUrl': 'URL de la app',
    'profile.field.youtubeUrl': 'URL de YouTube',
    'profile.field.instagramUrl': 'URL de Instagram',
    'profile.field.otherUrl': 'Otra URL',
    'profile.placeholder.username': 'Tu nombre de usuario',
    'profile.placeholder.currentPassword': 'Introduce tu contrasena actual',
    'profile.placeholder.newPassword': 'Introduce la nueva contrasena',
    'profile.placeholder.confirmNewPassword': 'Confirma la nueva contrasena',
    'profile.placeholder.fullName': 'Tu nombre completo',
    'profile.placeholder.phone': '+1 (555) 000-0000',
    'profile.placeholder.searchCountries': 'Escribe para buscar paises...',
    'profile.placeholder.biography': 'Cuentanos sobre ti...',
    'profile.placeholder.appName': 'p. ej., Mi App',
    'profile.placeholder.appUrl': 'https://miapp.com',
    'profile.placeholder.youtubeUrl': 'https://youtube.com/@tuusuario',
    'profile.placeholder.instagramUrl': 'https://instagram.com/tuusuario',
    'profile.placeholder.otherUrl': 'https://ejemplo.com',
    'profile.note.publicUsername': 'Tu nombre de usuario publico',
    'profile.note.emailImmutable': 'El correo no se puede cambiar desde el perfil',
    'profile.note.passwordMin': 'Minimo 6 caracteres',
    'profile.note.requiredForDashboard': 'Obligatorio para acceder al panel',
    'profile.note.countryRequired': 'Obligatorio para acceder al panel • Selecciona de la lista o escribe para buscar',
    'profile.note.avatarRequired': 'Obligatorio para acceder al panel • JPG, PNG o GIF (max 20MB)',
    'profile.note.biographyRequiredPrefix': 'Obligatorio para acceder al panel •',
    'profile.note.characters': 'caracteres',
    'profile.optional': 'Opcional',
    'profile.allOptional': 'Todo opcional',
    'profile.uploading': 'Subiendo...',
    'profile.uploadingAvatar': 'Subiendo avatar...',
    'profile.chooseAvatarImage': 'Elegir imagen de avatar',
    'profile.avatarPreviewAlt': 'Vista previa del avatar',
    'profile.preview': 'VISTA PREVIA',
    'profile.openInNewTab': 'Abrir en una nueva pestana',
    'profile.view': 'Ver',
    'profile.stats.rating': 'Valoracion',
    'profile.stats.credits': 'Creditos',
    'profile.stats.views': 'Vistas',
    'profile.stats.votes': 'Votos',
    'profile.saving': 'Guardando...',
    'profile.saveChanges': 'Guardar cambios',
    'profile.success.profileAndPasswordUpdated': 'Perfil y contrasena actualizados correctamente!',
    'profile.success.profileUpdatedComplete': 'Perfil actualizado correctamente! Todos los campos obligatorios estan completos. Ya puedes acceder al panel de Xperiences.',
    'profile.success.profileUpdatedIncomplete': 'Perfil actualizado correctamente! Completa todos los campos obligatorios (Nombre, Avatar, Biografia, Pais) para acceder al panel.',
    'profile.error.loadProfile': 'No se pudo cargar el perfil',
    'profile.error.avatarUpload': 'No se pudo subir el avatar. El perfil NO se guardo.',
    'profile.error.fillAllPasswordFields': 'Completa todos los campos de contrasena para cambiarla',
    'profile.error.passwordsMismatch': 'Las nuevas contrasenas no coinciden',
    'profile.error.passwordMin': 'La nueva contrasena debe tener al menos 6 caracteres',
    'profile.error.changePassword': 'No se pudo cambiar la contrasena',
    'profile.error.profileUpdatedPasswordFailed': 'Perfil actualizado pero el cambio de contrasena fallo',
    'profile.error.updateProfile': 'No se pudo actualizar el perfil. Intentalo de nuevo.',

    'adminCreators.title': 'Gestionar creadores',
    'adminCreators.loading': 'Cargando creadores...',
    'adminCreators.error.load': 'No se pudieron cargar los creadores',
    'adminCreators.error.roleUpdate': 'No se pudo actualizar el rol',
    'adminCreators.error.statusUpdate': 'No se pudo actualizar el estado',
    'adminCreators.error.trustUpdate': 'No se pudo actualizar el flag de confianza',
    'adminCreators.success.roleUpdated': 'Rol actualizado correctamente!',
    'adminCreators.success.statusUpdated': 'Estado actualizado correctamente!',
    'adminCreators.success.trustUpdated': 'Flag de confianza actualizado correctamente!',
    'adminCreators.table.alias': 'Alias',
    'adminCreators.table.email': 'Correo',
    'adminCreators.table.emailVerified': 'Correo verificado',
    'adminCreators.table.hidden': 'Oculto',
    'adminCreators.table.joinDate': 'Fecha de alta',
    'adminCreators.table.role': 'Rol',
    'adminCreators.table.status': 'Estado',
    'adminCreators.table.trusted': 'Trusted',
    'adminCreators.yes': 'Si',
    'adminCreators.no': 'No',
    'adminCreators.hidden': 'Oculto',
    'adminCreators.visible': 'Visible',
    'adminCreators.trusted': 'Trusted',
    'adminCreators.standard': 'Estandar',
    'adminCreators.role.creator': 'Creador',
    'adminCreators.role.manager': 'Manager',
    'adminCreators.role.administrator': 'Administrador',
    'adminCreators.status.pending': 'Pendiente',
    'adminCreators.status.approved': 'Aprobado',
    'adminCreators.status.rejected': 'Bloqueado / Rechazado',
    'adminCreators.empty': 'No se encontraron creadores.',
    'adminCreators.pagination.page': 'Pagina',
    'adminCreators.pagination.of': 'de',
    'adminCreators.pagination.total': 'Total',
    'adminCreators.pagination.rowsPerPage': 'Filas por pagina',
    'adminCreators.pagination.previous': 'Anterior',
    'adminCreators.pagination.next': 'Siguiente',

    'adminCreatorDetail.title': 'Editar creador:',
    'adminCreatorDetail.back': 'Volver',
    'adminCreatorDetail.id': 'ID',
    'adminCreatorDetail.email': 'Correo',
    'adminCreatorDetail.alias': 'Alias',
    'adminCreatorDetail.fullName': 'Nombre completo',
    'adminCreatorDetail.phone': 'Telefono',
    'adminCreatorDetail.countryCode': 'Codigo de pais',
    'adminCreatorDetail.countryPlaceholder': 'Escribe para buscar paises...',
    'adminCreatorDetail.flags': 'Flags del perfil',
    'adminCreatorDetail.emailVerified': 'Correo verificado',
    'adminCreatorDetail.resendEmail': 'Reenviar correo',
    'adminCreatorDetail.sending': 'Enviando...',
    'adminCreatorDetail.hideProfile': 'Ocultar perfil (incompleto)',
    'adminCreatorDetail.isTrusted': 'Creador trusted',
    'adminCreatorDetail.bio': 'Bio / Descripcion',
    'adminCreatorDetail.linksApps': 'Enlaces y apps',
    'adminCreatorDetail.appName': 'Nombre de la app',
    'adminCreatorDetail.appUrl': 'URL de la app',
    'adminCreatorDetail.youtubeUrl': 'URL de YouTube',
    'adminCreatorDetail.instagramUrl': 'URL de Instagram',
    'adminCreatorDetail.otherUrl': 'Otra URL',
    'adminCreatorDetail.saveProfile': 'Guardar perfil',
    'adminCreatorDetail.saving': 'Guardando...',
    'adminCreatorDetail.error.load': 'No se pudo cargar el creador',
    'adminCreatorDetail.error.verificationSend': 'No se pudo enviar el correo de verificacion.',
    'adminCreatorDetail.error.update': 'No se pudo actualizar el creador',
    'adminCreatorDetail.success.verificationSent': 'Correo de verificacion enviado correctamente.',
    'adminCreatorDetail.success.updated': 'Creador actualizado correctamente',

    'moderation.back': 'Volver',
    'moderation.title.single': 'Moderando contenido de Xperience',
    'moderation.title.global': 'Moderacion de contenido',
    'moderation.searchPlaceholder': 'Buscar por titulo, descripcion o creador...',
    'moderation.filter.allMedia': 'Todo el media',
    'moderation.filter.pending': 'Pendiente',
    'moderation.filter.approved': 'Aprobado',
    'moderation.filter.rejected': 'Rechazado',
    'moderation.loading': 'Cargando media...',
    'moderation.empty.search': 'No hay media que coincida con tu busqueda.',
    'moderation.empty.filter': 'No hay media para el filtro seleccionado.',
    'moderation.noPoster': 'Sin poster',
    'moderation.untitled': 'Sin titulo',
    'moderation.noDescription': 'Sin descripcion',
    'moderation.creator': 'Creador',
    'moderation.email': 'Correo',
    'moderation.date': 'Fecha',
    'moderation.na': 'N/D',
    'moderation.viewOriginal': 'Ver archivo original',
    'moderation.action.approve': 'Aprobar',
    'moderation.action.reject': 'Rechazar',
    'moderation.action.setPending': 'Poner pendiente',
    'moderation.success.markedAs': 'Media marcada correctamente como',
    'moderation.error.loadMedia': 'Error al cargar media para moderacion.',
    'moderation.error.updateStatus': 'No se pudo actualizar el estado del media.',

    'adminXperiences.backDashboard': 'Volver al panel',
    'adminXperiences.title': 'Moderacion global de Xperiences',
    'adminXperiences.searchPlaceholder': 'Buscar por titulo, descripcion o creador...',
    'adminXperiences.filter.all': 'Todas las Xperiences',
    'adminXperiences.filter.pending': 'Pendiente',
    'adminXperiences.filter.approved': 'Aprobado',
    'adminXperiences.filter.rejected': 'Rechazado',
    'adminXperiences.loading': 'Cargando xperiences...',
    'adminXperiences.empty.search': 'No hay xperiences que coincidan con tu busqueda.',
    'adminXperiences.empty.filter': 'No hay xperiences para el filtro seleccionado.',
    'adminXperiences.noPoster': 'Sin poster',
    'adminXperiences.noDescription': 'Sin descripcion',
    'adminXperiences.creator': 'Creador',
    'adminXperiences.email': 'Correo',
    'adminXperiences.date': 'Fecha',
    'adminXperiences.na': 'N/D',
    'adminXperiences.mediaCount': 'Cantidad de media',
    'adminXperiences.items': 'items',
    'adminXperiences.action.moderateMedia': 'Moderar media',
    'adminXperiences.action.approveAll': 'Aprobar todo',
    'adminXperiences.action.rejectAll': 'Rechazar todo',
    'adminXperiences.action.setPending': 'Poner pendiente',
    'adminXperiences.success.markedAs': 'Xperience marcada correctamente como',
    'adminXperiences.error.fetch': 'No se pudieron obtener las xperiences',
    'adminXperiences.error.updateStatus': 'No se pudo actualizar el estado',

    'createXperience.profileIncomplete': 'Completa tu perfil antes de crear una xperience',
    'createXperience.error.create': 'No se pudo crear la xperience',
    'createXperience.title': 'Crear nueva Xperience',
    'createXperience.subtitle': 'Empieza a construir tu contenido inmersivo',
    'createXperience.field.title': 'Titulo',
    'createXperience.placeholder.title': 'Titulo de la xperience',
    'createXperience.field.description': 'Descripcion',
    'createXperience.placeholder.description': 'Describe tu xperience...',
    'createXperience.field.location': 'Ubicacion',
    'createXperience.field.tags': 'Etiquetas',
    'createXperience.copyLastLocation': 'Copiar ultima localizacion',
    'createXperience.error.noLastLocation': 'Aun no hay una ubicacion previa disponible.',
    'createXperience.placeholder.tags': 'Selecciona o busca etiquetas...',
    'createXperience.help.tags': 'Elige una o varias etiquetas para mejorar el descubrimiento.',
    'createXperience.cancel': 'Cancelar',
    'createXperience.submit': 'Crear Xperience',

    'xperienceDetail.success.mediaOrderSaved': 'Orden del media guardado correctamente.',
    'xperienceDetail.error.mediaOrderSave': 'No se pudo guardar el orden del media.',
    'xperienceDetail.scenes.title': 'Escenas',
    'xperienceDetail.scenes.subtitle': 'Agrupa el media en momentos ordenados para la reproduccion en Vision Pro.',
    'xperienceDetail.scenes.newLabel': 'Nueva escena',
    'xperienceDetail.scenes.newPlaceholder': 'Por ejemplo: Ceremonia, Coctel, Baile...',
    'xperienceDetail.scenes.create': 'Crear escena',
    'xperienceDetail.scenes.save': 'Guardar',
    'xperienceDetail.scenes.moveUp': 'Subir',
    'xperienceDetail.scenes.moveDown': 'Bajar',
    'xperienceDetail.scenes.selectMedia': 'Selecciona media para anadir',
    'xperienceDetail.scenes.noAvailableMedia': 'Todo el media ya esta en esta escena',
    'xperienceDetail.scenes.addMedia': 'Anadir media',
    'xperienceDetail.scenes.removeMedia': 'Quitar',
    'xperienceDetail.scenes.emptyMedia': 'Aun no hay media en esta escena.',
    'xperienceDetail.scenes.empty': 'Aun no hay escenas. Crea escenas para organizar esta Xperience como reproduccion guiada.',
    'xperienceDetail.scenes.untitledMedia': 'Media sin titulo',
    'xperienceDetail.scenes.confirmDelete': 'Borrar esta escena? Los archivos de media seguiran disponibles en la Xperience.',
    'xperienceDetail.scenes.success.created': 'Escena creada.',
    'xperienceDetail.scenes.success.updated': 'Escena actualizada.',
    'xperienceDetail.scenes.success.deleted': 'Escena borrada.',
    'xperienceDetail.scenes.success.mediaAdded': 'Media anadido a la escena.',
    'xperienceDetail.scenes.success.mediaRemoved': 'Media quitado de la escena.',
    'xperienceDetail.scenes.error.save': 'No se pudo guardar la escena.',
    'xperienceDetail.scenes.error.reorder': 'No se pudo reordenar las escenas.',
    'xperienceDetail.scenes.error.reorderMedia': 'No se pudo reordenar el media de la escena.',
    'xperienceDetail.error.load': 'No se pudo cargar la xperience',
    'xperienceDetail.success.updated': 'Xperience actualizada correctamente.',
    'xperienceDetail.error.update': 'No se pudo actualizar la xperience',
    'xperienceDetail.sendingEmail': 'Enviando correo...',
    'xperienceDetail.success.emailSent': 'Correo con QR enviado correctamente.',
    'xperienceDetail.success.locationCopiedToAll': 'Ubicacion copiada a {count} elementos de media.',
    'xperienceDetail.error.sendEmail': 'No se pudo enviar el correo con QR.',
    'xperienceDetail.error.uploadMedia': 'No se pudo subir el media',
    'xperienceDetail.error.deleteMedia': 'No se pudo borrar el media',
    'xperienceDetail.error.deleteXperience': 'No se pudo borrar la xperience',
    'xperienceDetail.error.noLocationToCopyAll': 'Define una ubicacion primero antes de copiarla a todos los media.',
    'xperienceDetail.error.invalidLocationToCopyAll': 'Las coordenadas de la ubicacion actual son invalidas.',
    'xperienceDetail.error.noMediaToCopyLocationAll': 'No se encontraron elementos de media para actualizar en esta xperience.',
    'xperienceDetail.error.locationCopiedToAllPartial': 'Se actualizaron {success} elementos de media, pero {failed} fallaron.',
    'xperienceDetail.error.locationCopiedToAllFailed': 'No se pudo actualizar la ubicacion para todos los media.',
    'xperienceDetail.loading': 'Cargando xperience...',
    'xperienceDetail.notFound': 'Xperience no encontrada',
    'xperienceDetail.backDashboard': 'Volver al panel',
    'xperienceDetail.editTitle': 'Editar Xperience',
    'xperienceDetail.field.title': 'Titulo',
    'xperienceDetail.field.description': 'Descripcion',
    'xperienceDetail.field.location': 'Ubicacion',
    'xperienceDetail.field.tags': 'Etiquetas',
    'xperienceDetail.copyLastLocation': 'Copiar ultima localizacion',
    'xperienceDetail.copyLocationToAll': 'Copiar ubicacion a todos los media',
    'xperienceDetail.copyLocationToAllLoading': 'Copiando a todos los media...',
    'xperienceDetail.error.noLastLocation': 'Aun no hay una ubicacion previa disponible.',
    'xperienceDetail.success.lastLocationCopied': 'Ultima ubicacion copiada.',
    'xperienceDetail.placeholder.tags': 'Selecciona o busca etiquetas...',
    'xperienceDetail.help.tags': 'Elige una o varias etiquetas para mejorar el descubrimiento.',
    'xperienceDetail.privateToggle': 'Xperience privada (requiere contrasena)',
    'xperienceDetail.cancel': 'Cancelar',
    'xperienceDetail.saveChanges': 'Guardar cambios',
    'xperienceDetail.edit': 'Editar',
    'xperienceDetail.deleteXperience': 'Borrar Xperience',
    'xperienceDetail.info.status': 'Estado',
    'xperienceDetail.pending': 'Pendiente',
    'xperienceDetail.info.views': 'Vistas',
    'xperienceDetail.info.rating': 'Valoracion',
    'xperienceDetail.info.reactions': 'Reacciones',
    'xperienceDetail.info.tags': 'Etiquetas',
    'xperienceDetail.info.category': 'Categoria',
    'xperienceDetail.info.created': 'Creada',
    'xperienceDetail.noDate': 'N/D',
    'xperienceDetail.info.mediaCount': 'Cantidad de media',
    'xperienceDetail.info.visibility': 'Visibilidad',
    'xperienceDetail.visibility.private': 'Privada',
    'xperienceDetail.visibility.public': 'Publica',
    'xperienceDetail.privateQR.title': 'QR de acceso privado',
    'xperienceDetail.privateQR.description': 'Comparte este QR o contrasena para dar acceso privado a tu xperience.',
    'xperienceDetail.privateQR.alt': 'Codigo QR de acceso privado',
    'xperienceDetail.success.passwordCopied': 'Contrasena copiada al portapapeles.',
    'xperienceDetail.copyPassword': 'Copiar contrasena',
    'xperienceDetail.wait': 'Espera',
    'xperienceDetail.emailQR': 'Enviar QR por correo',
    'xperienceDetail.mediaSectionTitle': 'Media de la Xperience',
    'xperienceDetail.createMedia': '+ Crear media',
    'xperienceDetail.uploading': 'Subiendo',
    'xperienceDetail.noMedia': 'Aun no hay media.',
    'xperienceDetail.noMediaHelp': 'Aun no hay archivos de media. Sube imagenes, videos o audio.',
    'xperienceDetail.mediaCard.added': 'Anadido',
    'xperienceDetail.mediaCard.na': 'N/D',
    'xperienceDetail.mediaCard.viewDetails': 'Ver detalles',
    'xperienceDetail.confirm.deleteMediaTitle': 'Borrar media?',
    'xperienceDetail.confirm.deleteMediaMessage': 'Este elemento de media se eliminara de forma permanente. Esta accion no se puede deshacer.',
    'xperienceDetail.confirm.deleteXperienceTitle': 'Borrar xperience?',
    'xperienceDetail.confirm.deleteXperienceMessage': 'Esta xperience y todo su media asociado se eliminaran de forma permanente. Esta accion no se puede deshacer.',
    'xperienceDetail.confirm.delete': 'Borrar',
    'xperienceDetail.confirm.cancel': 'Cancelar',
    'xperienceDetail.confirm.copyLocationAllTitle': 'Copiar ubicacion a todos los media?',
    'xperienceDetail.confirm.copyLocationAllMessage': 'Esto actualizara las coordenadas de ubicacion para todos los media de esta xperience. Continuar?',
    'xperienceDetail.confirm.copyLocationAllConfirm': 'Si, actualizar todos los media',
    'xperienceDetail.confirm.copyLocationAllCancel': 'Cancelar',

    'xmedia.error.loadData': 'No se pudieron cargar los datos',
    'xmedia.error.delete': 'No se pudo borrar el media',
    'xmedia.error.save': 'No se pudo guardar el media de la xperience',
    'xmedia.error.uploadTooLarge': 'El archivo media es demasiado grande para el limite actual de subida.',
    'xmedia.error.noXperienceLocation': 'La xperience padre aun no tiene una ubicacion para copiar.',
    'xmedia.error.noLastLocation': 'Aun no hay una ubicacion previa disponible.',
    'xmedia.error.requiredPrefix': 'Los siguientes campos son obligatorios:',
    'xmedia.success.posterSelected': 'Poster seleccionado localmente. Se subira al guardar.',
    'xmedia.success.mediaSelected': 'Media seleccionado localmente. Se subira al guardar.',
    'xmedia.success.deleted': 'Media borrado correctamente!',
    'xmedia.success.created': 'Media de la xperience creado y archivos subidos correctamente!',
    'xmedia.success.updated': 'Media de la xperience actualizado correctamente!',
    'xmedia.success.locationCopied': 'Ubicacion copiada desde la xperience.',
    'xmedia.success.lastLocationCopied': 'Ultima ubicacion copiada.',
    'xmedia.confirm.deleteMessage': 'Seguro que deseas borrar este media y sus archivos asociados de forma permanente? Esta accion no se puede deshacer.',
    'xmedia.loading': 'Cargando media de la xperience...',
    'xmedia.notFound': 'No se encontro el media de la xperience',
    'xmedia.back': 'Volver',
    'xmedia.title.create': 'Crear nuevo media',
    'xmedia.title.edit': 'Editar media de la xperience',
    'xmedia.subtitle': 'Actualiza todos los metadatos y archivos de este elemento de media.',
    'xmedia.field.title': 'Titulo',
    'xmedia.field.description': 'Descripcion',
    'xmedia.field.type': 'Tipo',
    'xmedia.field.format': 'Formato',
    'xmedia.field.category': 'Categoria',
    'xmedia.field.mediaFile': 'Archivo media',
    'xmedia.field.posterUrl': 'URL del poster',
    'xmedia.field.order': 'Orden',
    'xmedia.field.runtime': 'Duracion',
    'xmedia.field.credits': 'Creditos / puntos requeridos',
    'xmedia.field.location': 'Ubicacion',
    'xmedia.field.createdDate': 'Fecha de creacion',
    'xmedia.placeholder.title': 'Introduce un titulo',
    'xmedia.placeholder.description': 'Introduce una descripcion...',
    'xmedia.placeholder.mediaUrl': 'La URL se generara de forma segura al guardar',
    'xmedia.placeholder.posterUrl': 'La ruta del poster se generara al guardar',
    'xmedia.placeholder.order': 'p.ej., 1',
    'xmedia.placeholder.runtime': 'p.ej., 3600',
    'xmedia.placeholder.credits': 'p.ej., 0',
    'xmedia.selectFile': 'Seleccionar archivo',
    'xmedia.changeFile': 'Cambiar archivo',
    'xmedia.viewMedia': 'Ver media',
    'xmedia.selectImage': 'Seleccionar imagen',
    'xmedia.changeImage': 'Cambiar imagen',
    'xmedia.thumbnail': 'Imagen miniatura',
    'xmedia.seconds': 'segundos',
    'xmedia.requiresDownload': 'Requiere descarga',
    'xmedia.isPublic': 'Es publico',
    'xmedia.isPremium': 'Es premium',
    'xmedia.hide': 'Ocultar',
    'xmedia.deleteMedia': 'Borrar media',
    'xmedia.cancel': 'Cancelar',
    'xmedia.saving': 'Guardando...',
    'xmedia.saveChanges': 'Guardar cambios',
    'xmedia.copyXperienceLocation': 'Copiar ubicacion de la xperience',
    'xmedia.copyLastLocation': 'Copiar ultima localizacion',
    'xmedia.copyLocationToAll': 'Copiar ubicacion a todos los media',
    'xmedia.copyLocationToAllLoading': 'Copiando a todos los media...',
    'xmedia.locationSource.xperience': 'Usando ubicacion primaria de la xperience',
    'xmedia.locationSource.last': 'Usando ultima ubicacion copiada',
    'xmedia.lastLocationPreview': 'Ultima ubicacion guardada',
    'xmedia.confirm.copyLocationAllTitle': 'Copiar ubicacion a todos los media?',
    'xmedia.confirm.copyLocationAllMessage': 'Esto actualizara las coordenadas de ubicacion para todos los media de esta xperience. Continuar?',
    'xmedia.confirm.copyLocationAllConfirm': 'Si, actualizar todos los media',
    'xmedia.confirm.copyLocationAllCancel': 'Cancelar',
    'xmedia.success.locationCopiedToAll': 'Ubicacion copiada a {count} elementos de media.',
    'xmedia.upload.selected': 'Seleccionado',
    'xmedia.upload.keepOpen': 'Mantén esta pestaña del navegador abierta hasta que termine la subida.',
    'xmedia.upload.poster': 'Subida del poster',
    'xmedia.upload.media': 'Subida del media',
    'xmedia.upload.speed': 'Velocidad',
    'xmedia.upload.remaining': 'Tiempo restante',
    'xmedia.upload.phase.idle': 'Listo para subir',
    'xmedia.upload.phase.metadata': 'Guardando metadatos del media...',
    'xmedia.upload.phase.poster': 'Subiendo poster...',
    'xmedia.upload.phase.media': 'Subiendo media inmersivo...',
    'xmedia.upload.phase.finalizing': 'Finalizando subida...',
    'xmedia.error.noXperienceForBulkLocation': 'No se pudo encontrar la xperience padre para la actualizacion masiva.',
    'xmedia.error.noLocationToCopyAll': 'Define una ubicacion primero antes de copiarla a todos los media.',
    'xmedia.error.invalidLocationToCopyAll': 'Las coordenadas de la ubicacion actual son invalidas.',
    'xmedia.error.noMediaToCopyLocationAll': 'No se encontraron elementos de media para actualizar en esta xperience.',
    'xmedia.error.locationCopiedToAllPartial': 'Se actualizaron {success} elementos de media, pero {failed} fallaron.',
    'xmedia.error.locationCopiedToAllFailed': 'No se pudo actualizar la ubicacion para todos los media.',

    'locationPicker.searchLabel': 'Buscar direccion',
    'locationPicker.searchPlaceholder': 'Escribe una direccion, ciudad o lugar',
    'locationPicker.longitude': 'Longitud',
    'locationPicker.latitude': 'Latitud',
    'locationPicker.clear': 'Limpiar ubicacion',
    'locationPicker.useCurrent': 'Usar mi ubicacion actual',
    'locationPicker.locating': 'Localizando...',
    'locationPicker.geoNotSupported': 'Tu navegador no soporta geolocalizacion.',
    'locationPicker.geoError': 'No se pudo obtener tu ubicacion actual. Revisa los permisos del navegador.',
    'locationPicker.selectedAddress': 'Direccion seleccionada',
    'locationPicker.loadingMap': 'Cargando mapa...',
    'locationPicker.noApiKey': 'Falta la API key de Google Maps',
    'locationPicker.noApiKeyHelp': 'Define REACT_APP_GOOGLE_MAPS_API_KEY en el entorno del frontend para activar mapa y busqueda por direccion.',
    'locationPicker.loadError': 'No se pudo cargar Google Maps',
    'locationPicker.hint': 'Tip: busca una direccion, haz click en el mapa o arrastra el marcador para ajustar coordenadas.'
  },
  ...Object.fromEntries(
    Object.entries(generatedTranslations).map(([locale, bundle]) => [
      locale,
      {
        ...bundle,
        ...(localeManualOverrides[locale] || {})
      }
    ])
  )
};

const localeAliases: Record<string, SupportedLocale> = {
  en: 'en',
  'en-us': 'en',
  'en-au': 'en-AU',
  'en-gb': 'en-GB',
  es: 'es',
  'es-es': 'es',
  'es-419': 'es',
  fr: 'fr',
  'fr-fr': 'fr',
  'fr-ca': 'fr-CA',
  de: 'de',
  'de-de': 'de',
  ja: 'ja',
  'ja-jp': 'ja',
  zh: 'zh-Hans',
  'zh-cn': 'zh-Hans',
  'zh-sg': 'zh-Hans',
  'zh-hans': 'zh-Hans',
  'zh-tw': 'zh-Hant',
  'zh-hk': 'zh-HK',
  'zh-hant': 'zh-Hant'
};

function normalizeLocale(rawLocale: string): SupportedLocale {
  const lowered = rawLocale.toLowerCase();

  if (localeAliases[lowered]) {
    return localeAliases[lowered];
  }

  const short = lowered.split('-')[0];
  if (localeAliases[short]) {
    return localeAliases[short];
  }

  return 'en';
}

export function getCurrentLocale(): SupportedLocale {
  const preferred = getPreferredLocale();
  if (preferred) {
    return preferred;
  }

  if (typeof window === 'undefined' || !window.navigator?.language) {
    return 'en';
  }

  return normalizeLocale(window.navigator.language);
}

export function getPreferredLocale(): SupportedLocale | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (!saved) {
      return null;
    }
    return normalizeLocale(saved);
  } catch {
    return null;
  }
}

export function setPreferredLocale(locale: string): SupportedLocale {
  const normalized = normalizeLocale(locale);

  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, normalized);
    } catch {
      // Ignore storage issues and keep runtime locale only.
    }
  }

  return normalized;
}

function getMessageBundle(locale: SupportedLocale): Messages {
  return messages[locale] || (locale.startsWith('es') ? messages.es : messages.en);
}

export function t(key: string, fallback?: string): string {
  const locale = getCurrentLocale();
  const bundle = getMessageBundle(locale);
  const enBundle = messages.en;

  return bundle[key] || enBundle[key] || fallback || key;
}

export function getLegalFileLocale(): string {
  const locale = getCurrentLocale();

  // Keep locale-specific legal files where they exist.
  if (SUPPORTED_LOCALES.includes(locale)) {
    return locale;
  }

  return locale.startsWith('es') ? 'es' : 'en';
}
